import { useMemo } from 'react';

import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import { GridColDef, ValueOptions } from '@mui/x-data-grid-pro';

import BooleanCell from 'components/shared/Table/TableCell/DataGrid/BooleanCell';
import ColorCell from 'components/shared/Table/TableCell/DataGrid/ColorCell';
import DateCell from 'components/shared/Table/TableCell/DataGrid/DateCell';

import useGetFunctionGroupFilterOptions from './useGetFunctionGroupFilterOptions';

const useColumnDefinitions = () => {
  const { t } = useTranslation();

  const functionGroups = useGetFunctionGroupFilterOptions();

  return useMemo(() => {
    return generateColumns(t, functionGroups);
  }, [t, functionGroups]);
};

const generateColumns = (
  t: TFunction,
  functionGroups: ValueOptions[]
): GridColDef[] => [
  {
    field: 'code',
    headerName: t('functions.fields.code'),
    type: 'text',
    hideable: false
  },
  {
    field: 'description',
    minWidth: 200,
    flex: 1,
    headerName: t('functions.fields.description')
  },
  {
    field: 'colorCode',
    headerName: t('functions.fields.colorCode'),
    renderCell: ColorCell,
    headerAlign: 'center',
    maxWidth: 70,
    align: 'center'
  },
  {
    field: 'codeExt',
    maxWidth: 100,
    headerName: t('functions.fields.codeExt'),
    type: 'text'
  },
  {
    field: 'costKind',
    maxWidth: 100,
    headerName: t('functions.fields.costKind'),
    type: 'number'
  },
  {
    field: 'isGroup',
    maxWidth: 70,
    headerName: t('functions.fields.flags.FUNCTION_GROUP'),
    type: 'boolean',
    renderCell: BooleanCell
  },
  {
    field: 'functionGroup',
    type: 'singleSelect',
    flex: 1,
    headerName: t('functions.fields.parentFunctionId'),
    valueOptions: functionGroups
  },
  {
    field: 'noPicker',
    maxWidth: 70,
    headerName: t('functions.fields.flags.REQUEST_EXCLUDE'),
    type: 'boolean',
    renderCell: BooleanCell
  },
  {
    field: 'excludeCapPlan',
    maxWidth: 70,
    headerName: t('functions.fields.flags.CAPPLAN_EXCLUDE'),
    type: 'boolean',
    renderCell: BooleanCell
  },
  {
    field: 'noBUH',
    maxWidth: 70,
    headerName: t('functions.fields.flags.BUH_BLOCKED'),
    type: 'boolean',
    renderCell: BooleanCell
  },
  {
    field: 'noBonus',
    maxWidth: 70,
    headerName: t('functions.fields.flags.BONUS_BLOCKED'),
    type: 'boolean',
    renderCell: BooleanCell
  },
  {
    field: 'leaveBlocked',
    maxWidth: 70,
    headerName: t('functions.fields.flags.LEAVE_BLOCKED'),
    type: 'boolean',
    renderCell: BooleanCell
  },
  {
    field: 'salaryBlocked',
    maxWidth: 70,
    headerName: t('functions.fields.flags.SALARY_BLOCKED'),
    type: 'boolean',
    renderCell: BooleanCell
  },
  {
    field: 'overlap',
    maxWidth: 70,
    headerName: t('functions.fields.flags.DUTY_OVERLAP_ALLOWED'),
    type: 'boolean',
    renderCell: BooleanCell
  },
  {
    field: 'includeInKidsRatio',
    maxWidth: 70,
    headerName: t('functions.fields.flags.INCLUDE_IN_KIDS_RATIO'),
    type: 'boolean',
    renderCell: BooleanCell
  },
  {
    field: 'from',
    flex: 1,
    maxWidth: 120,
    headerName: t('validFrom'),
    type: 'date',
    renderCell: DateCell
  },
  {
    field: 'to',
    flex: 1,
    maxWidth: 120,
    headerName: t('validTo'),
    type: 'date',
    renderCell: DateCell
  }
];

export default useColumnDefinitions;
